import React, { useState, useRef, useEffect } from "react";
import ReactMarkdown from "react-markdown";
import { 
  GraduationCap, 
  BookOpen, 
  FileText, 
  Image, 
  Send, 
  X, 
  Trash2, 
  ExternalLink, 
  Loader2, 
  Sparkles, 
  ShieldAlert, 
  HelpCircle,
  FileCheck2,
  Minimize2,
  Maximize2,
  Plus
} from "lucide-react";

import { Patient, ClinicalRecord } from "../types";

interface DocumentUpload {
  name: string;
  type: string;
  size: number;
  base64: string;
}

interface Message {
  role: "user" | "model";
  text: string;
  timestamp: string;
  references?: { title: string; url: string }[];
  fileAttached?: { name: string; type: string };
}

interface ClinicalProfessorBarProps {
  apiToken: string;
  patient?: Patient | null;
  clinicalRecords?: ClinicalRecord[];
  patientDocuments?: any[];
  currentConsultation?: {
    subjective: string;
    objective: string;
    assessment: string;
    plan: string;
    gpNotes: string;
    icd10: string;
    diagnoses: string;
  };
}

export default function ClinicalProfessorBar({ apiToken, patient, clinicalRecords, patientDocuments, currentConsultation }: ClinicalProfessorBarProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const [inputText, setInputText] = useState("");
  const [attachedFile, setAttachedFile] = useState<DocumentUpload | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [chatHistory, setChatHistory] = useState<Message[]>([]);
  const chatBottomRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Auto scroll to latest reply
  useEffect(() => {
    if (chatBottomRef.current) {
      chatBottomRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [chatHistory, isLoading]);

  // Read files to base64
  const processFile = (file: File) => {
    try {
      if (file.size > 8 * 1024 * 1024) {
        alert("File size exceeded the limit (8MB). Please attach a smaller file.");
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setAttachedFile({
          name: file.name,
          type: file.type,
          size: file.size,
          base64: reader.result as string
        });
      };
      reader.readAsDataURL(file);
    } catch (err) {
      console.error("Clinical file reader error:", err);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) processFile(file);
  };

  const removeAttachedFile = () => {
    setAttachedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // Quick Action Prompts
  const handleQuickAction = (actionType: string) => {
    let promptText = "";
    if (actionType === "SOAP_DIFF") {
      promptText = "Please review the case diagnosis and construct a comprehensive Differential Diagnosis based on the following SOAP details:\nSubjective:\nObjective:\nAssessment:";
    } else if (actionType === "LAB_IMAGE") {
      promptText = "Review the attached file or image (e.g. lab results, radiology, skin lesions) to analyze clinical criteria, and provide me with the main indicators and warning signs.";
    } else if (actionType === "INTERACTIONS") {
      promptText = "Review the following medication directive and inspect potential drug interactions and the safe dosage approved by the MOH:\nMedication Name and Dosage:";
    } else if (actionType === "PEDIATRICS") {
      promptText = "Examine this pediatric developmental chart and evaluate whether it falls within the normal range according to WHO Growth standards.";
    } else if (actionType === "FULL_HISTORY") {
      promptText = "Please analyze the complete medical history of this patient, including a careful examination of all attached medical images, X-rays, and lab reports. Correlate the findings from these attachments with the clinical records. Summarize the condition trajectory, highlight chronic issues, assess response to previous treatments, and flag any clinical 'Red Flags'. Keep the response concise, focusing only on the case and the most important findings without unnecessary details. Format your response in a clear, professional structure with bullet points.";
    }
    setInputText(promptText);
    if (!isOpen) setIsOpen(true);
  };

  // Submit chat query to the Clinical Professor AI server
  const handleSendMessage = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!inputText.trim() && !attachedFile) return;

    const userMsgText = inputText;
    const currentAttachment = attachedFile;
    
    // Clear inputs immediately
    setInputText("");
    removeAttachedFile();
    setIsLoading(true);

    const newUserMessage: Message = {
      role: "user",
      text: userMsgText || "Analyzing the attached medical file...",
      timestamp: new Date().toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }),
      fileAttached: currentAttachment ? { name: currentAttachment.name, type: currentAttachment.type } : undefined
    };

    setChatHistory(prev => [...prev, newUserMessage]);

    try {
      const response = await fetch("/api/clinical/professor-chat", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${apiToken}`
        },
        body: JSON.stringify({
          message: userMsgText,
          history: chatHistory.map(h => ({ role: h.role, text: h.text })),
          file: currentAttachment?.base64,
          fileType: currentAttachment?.type,
          additionalFiles: userMsgText.includes("complete medical history of this patient") && patientDocuments ? patientDocuments.map(doc => ({
            fileBase64: doc.fileData || doc.fileBase64,
            fileUrl: doc.fileUrl,
            fileType: doc.fileType
          })) : undefined,
          sourceGrounding: "ALL",
          patientContext: patient ? {
            name: patient.name,
            age: patient.age || patient.dob ? new Date().getFullYear() - new Date(patient.dob!).getFullYear() : undefined,
            gender: patient.gender,
            bloodType: patient.bloodType,
            allergies: patient.allergies,
            currentOpenConsultation: currentConsultation,
            clinicalRecords: clinicalRecords?.map(r => ({
              assessment: r.soapAssessment,
              objective: r.soapObjective,
              subjective: r.soapSubjective,
              plan: r.soapPlan,
              prescriptions: r.prescriptions
            }))
          } : undefined
        })
      });

      if (!response.ok) {
        let errMessage = "Failed to connect to the Clinical Professor Assistant server.";
        try {
          const errData = await response.json();
          if (errData.error) errMessage = errData.error + (errData.details ? ` (${errData.details})` : "");
        } catch(e) {}
        throw new Error(errMessage);
      }

      const resData = await response.json();
      
      const clinicalProfessorResponse: Message = {
        role: "model",
        text: resData.reply,
        timestamp: new Date().toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }),
        references: resData.references
      };

      setChatHistory(prev => [...prev, clinicalProfessorResponse]);
    } catch (err: any) {
      console.error("Clinical Professor consultation pipeline failed:", err);
      setChatHistory(prev => [
        ...prev,
        {
          role: "model",
          text: `⚠️ **Technical Clinical Error:** Could not analyze the clinical case because of intelligent server response issues.\n\n_Details:_ ${err.message || "Unstable clinic network response."}`,
          timestamp: new Date().toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })
        }
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const getSourceLabel = (code: string) => {
    switch (code) {
      case "WHO": return "منظمة الصحة العالمية / WHO";
      case "PUBMED": return "المكتبة الطبية الوطنية / PubMed";
      case "MAYOCLINIC": return "مايو كلينك الطبي / Mayo Clinic";
      case "CDC": return "مكافحة الأوبئة / CDC";
      case "LANCET": return "لانست وUpToDate / Clinical Lancet";
      default: return "قاعدة البيانات الطبية الشاملة / All Trusted Libraries";
    }
  };

   return (
    <div id="clinical-professor-cdss" className="space-y-4">
      
      {/* 1. Launcher Badge / Widget Card */}
      <div className="bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 border border-indigo-500/30 rounded-xl p-4 shadow-md text-white text-left">
        <div className="flex items-center justify-between border-b border-indigo-500/20 pb-2.5 mb-3">
          <div className="flex items-center gap-2">
            {/* Alaa Professor Avatar */}
            <div className="relative shrink-0">
              <div className="absolute inset-0 rounded-full bg-indigo-500/40 animate-ping opacity-60 scale-110" />
              <img
                src="/alaa_professor.png"
                alt="آلاء البروفيسورة"
                className="relative w-9 h-9 rounded-full object-cover border-2 border-indigo-400/60 shadow-lg shadow-indigo-900/50"
                referrerPolicy="no-referrer"
              />
            </div>
            <div>
              <h4 className="text-xs font-black tracking-wide leading-tight">Clinical Professor Advisor</h4>
              <p className="text-[8.5px] text-indigo-300 font-semibold leading-tight mt-0.5">آلاء • مستشارة طبية ذكية</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="bg-indigo-500/20 text-indigo-300 font-mono font-bold text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded border border-indigo-400/20">
              Professor AI • CDSS
            </span>
            <span id="ai-active-pulse" className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-indigo-500"></span>
            </span>
          </div>
        </div>


        <p className="text-[10.5px] text-slate-300 leading-relaxed mb-3 font-medium">
          Academic AI consultant dedicated to supporting clinical decision-making for advanced diagnosis, drug interaction checks, and medical research matching through verified clinical resources and patient attachments.
        </p>

        {/* Quick Diagnostic Triggers */}
        <div className="grid grid-cols-2 gap-1.5 mb-3">
          <button 
            type="button"
            onClick={() => handleQuickAction("SOAP_DIFF")}
            className="text-[9.5px] font-bold bg-white/5 border border-white/10 hover:bg-indigo-500/20 hover:border-indigo-400/40 py-1 px-1.5 rounded transition text-left truncate cursor-pointer"
          >
            📋 Draft Diff. Diagnosis
          </button>
          <button 
            type="button"
            onClick={() => handleQuickAction("LAB_IMAGE")}
            className="text-[9.5px] font-bold bg-white/5 border border-white/10 hover:bg-indigo-500/20 hover:border-indigo-400/40 py-1 px-1.5 rounded transition text-left truncate cursor-pointer"
          >
            🧬 Review Lab/Radiology
          </button>
          <button 
            type="button"
            onClick={() => handleQuickAction("INTERACTIONS")}
            className="text-[9.5px] font-bold bg-white/5 border border-white/10 hover:bg-indigo-500/20 hover:border-indigo-400/40 py-1 px-1.5 rounded transition text-left truncate cursor-pointer"
          >
            💊 Drug Interactions 
          </button>
          <button 
            type="button"
            onClick={() => handleQuickAction("PEDIATRICS")}
            className="text-[9.5px] font-bold bg-white/5 border border-white/10 hover:bg-indigo-500/20 hover:border-indigo-400/40 py-1 px-1.5 rounded transition text-left truncate cursor-pointer"
          >
            👶 Child Dev. & Growth
          </button>
        </div>

        <button 
          type="button"
          onClick={() => handleQuickAction("FULL_HISTORY")}
          className="w-full text-[10.5px] font-black bg-teal-500/10 border border-teal-400/30 hover:bg-teal-500/20 hover:border-teal-400/60 py-1.5 px-2 rounded-lg transition text-teal-300 mb-3 shadow-sm cursor-pointer flex items-center justify-center gap-1.5"
        >
          ⏱️ Analyze Full Patient History
        </button>

        <button
          id="open-professor-panel-btn"
          type="button"
          onClick={() => {
            setIsOpen(true);
            if (chatHistory.length === 0) {
              setChatHistory([
                {
                  role: "model",
                  text: "Welcome. I am the **Medical Consultant for IQ-Doctor Smart Clinic**, under the supervision of **Dr. Watheq Al-Chalabi**.\n\nI stand by you to discuss complex cases and construct Differential Diagnoses. You can write your inquiry now, or **drag and drop medical documents, radiology scans, and lab results** for immediate analysis and validation of the treatment plan using trusted global databases.",
                  timestamp: new Date().toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })
                }
              ]);
            }
          }}
          className="w-full bg-indigo-600 hover:bg-indigo-500 text-white text-[11px] font-black py-1.8 px-3 rounded-lg transition-all duration-150 inline-flex items-center justify-center gap-1.5 shadow-sm active:scale-98 cursor-pointer"
        >
          <Sparkles className="w-3.5 h-3.5" />
          <span>Consult Clinical Professor (Open Advisor Panel)</span>
        </button>
      </div>

      {/* 2. Collapsible Drawer Panel */}
      {isOpen && (
        <div className={`fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex justify-end transition-opacity duration-200`}>
          
          {/* Main workspace container */}
          <div 
            id="clinical-professor-drawer"
            className={`bg-white h-full shadow-2xl flex flex-col transition-all duration-300 ${
              isMaximized ? "w-full md:w-[90vw]" : "w-full md:w-[540px] border-l border-indigo-100"
            }`}
          >
            
            {/* Drawer Header */}
            <div className="bg-[#0F172A] text-white p-4 flex items-center justify-between border-b border-slate-800">
              <div className="flex items-center gap-2.5 text-left">
                <div className="bg-indigo-600/30 p-2 rounded-lg text-indigo-400 border border-indigo-500/20">
                  <GraduationCap className="w-5 h-5 animate-bounce" />
                </div>
                <div>
                  <h3 className="text-xs font-black tracking-wide uppercase flex items-center justify-start gap-1.5">
                    IQ-Doctor Consultant - Smart Medical Reference
                    <span className="text-[10px] text-teal-400 font-normal bg-teal-950/40 border border-teal-500/20 px-1.5 py-0.2 rounded font-mono">
                      CDSS ACTIVE
                    </span>
                  </h3>
                  <p className="text-[9.5px] text-indigo-300 font-semibold mt-0.5">
                    Academic AI Professor verified by leading medical research libraries
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-1.5">
                <button 
                  type="button"
                  onClick={() => setIsMaximized(!isMaximized)}
                  className="p-1 rounded-md text-slate-400 hover:text-white hover:bg-slate-800 transition cursor-pointer hidden md:inline-block"
                  title={isMaximized ? "Minimize" : "Maximize"}
                >
                  {isMaximized ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
                </button>
                <button 
                  type="button"
                  onClick={() => setIsOpen(false)}
                  className="p-1 rounded-md text-slate-400 hover:text-white hover:bg-slate-800 transition cursor-pointer"
                  title="Close Panel"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Chat Messages Thread panel */}
            <div className="flex-1 p-4 overflow-y-auto space-y-4 bg-slate-50/40 border-t border-slate-200">
              {chatHistory.map((msg, idx) => (
                <div 
                  key={idx}
                  className={`flex flex-col max-w-[90%] text-left ${
                    msg.role === "user" ? "ml-auto items-end" : "mr-auto items-start"
                  }`}
                >
                  {/* Sender Banner */}
                  <span className="text-[9.5px] text-slate-400 font-mono mb-1 px-1">
                    {msg.role === "user" ? "Attending Physician / MD User" : "IQ-Doctor Consultant (AI)"} • {msg.timestamp}
                  </span>

                  {/* Bubble body */}
                  <div 
                    className={`p-3 rounded-2xl border ${
                      msg.role === "user"
                        ? "bg-indigo-600 text-white border-indigo-700 rounded-tr-none text-left"
                        : "bg-white text-slate-800 border-indigo-100 rounded-tl-none font-medium leading-relaxed"
                    }`}
                    dir="ltr"
                  >
                    {msg.fileAttached && (
                      <div className="mb-2 p-1.5 bg-slate-100/10 border border-dashed border-white/20 rounded-lg text-[10px] flex items-center gap-1.5 bg-indigo-950/20 text-indigo-200">
                        <FileCheck2 className="w-3.5 h-3.5" />
                        <span className="font-bold truncate max-w-[200px]">{msg.fileAttached.name}</span>
                        <span className="text-[8px] bg-slate-200/20 px-1 py-0.1 rounded font-mono uppercase inline">
                          {msg.fileAttached.type.split("/")[1] || "DOC"}
                        </span>
                      </div>
                    )}

                    {msg.role === "user" ? (
                      <p className="whitespace-pre-wrap text-xs font-bold leading-relaxed">{msg.text}</p>
                    ) : (
                      <div className="markdown-body text-xs space-y-2 text-left">
                        <ReactMarkdown>{msg.text}</ReactMarkdown>
                      </div>
                    )}
                  </div>

                  {/* Citation References panel */}
                  {msg.references && msg.references.length > 0 && (
                    <div className="mt-1.5 w-full bg-slate-100/50 border border-slate-200 rounded-lg p-2 max-w-[420px]">
                      <span className="text-[8px] font-black text-slate-500 uppercase tracking-wider block mb-1">
                        🔍 Trusted Medical Literature Citations:
                      </span>
                      <div className="flex flex-wrap gap-1.5">
                        {msg.references.map((ref, rIdx) => (
                          <a
                            key={rIdx}
                            href={ref.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[9.5px] text-indigo-600 hover:text-indigo-800 font-bold bg-white px-1.5 py-0.5 rounded border border-slate-200 hover:border-indigo-400 transition-all inline-flex items-center gap-1"
                          >
                            <ExternalLink className="w-2.5 h-2.5 shrink-0" />
                            <span>{ref.title || "Reference study"}</span>
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}

              {isLoading && (
                <div className="flex flex-col items-start max-w-[85%] mr-auto">
                  <span className="text-[9.5px] text-slate-400 font-mono mb-1">
                    IQ-Doctor Consultant is reviewing references now...
                  </span>
                  <div className="bg-indigo-50 border border-indigo-150 p-4 rounded-xl rounded-tl-none text-left flex items-center gap-3">
                    <Loader2 className="w-4 h-4 text-indigo-600 animate-spin shrink-0" />
                    <div>
                      <p className="text-xs font-bold text-indigo-950">
                        The Professor is querying medical calibrations and clinical libraries
                      </p>
                      <p className="text-[9.5px] text-slate-500 mt-0.5 leading-relaxed">
                        Secure cloud search and kinetic examination across all approved medical references are underway to verify interactions and ensure the highest clinical safety levels.
                      </p>
                    </div>
                  </div>
                </div>
              )}
              <div ref={chatBottomRef} />
            </div>

            {/* Drags-and-Drop / File Upload section & Message Inputs */}
            <div className="border-t border-indigo-100 p-3 bg-white space-y-2.5">
              
              {/* File upload previews */}
              {attachedFile && (
                <div className="flex items-center justify-between p-2 bg-indigo-50 border border-indigo-200 rounded-lg text-xs">
                  <div className="flex items-center gap-1.5">
                    {attachedFile.type.startsWith("image/") ? (
                      <div className="relative w-9 h-9 border border-indigo-300 rounded overflow-hidden shadow-xs shrink-0 bg-white">
                        <img 
                          src={attachedFile.base64} 
                          alt="preview" 
                          referrerPolicy="no-referrer"
                          className="w-full h-full object-cover" 
                        />
                      </div>
                    ) : (
                      <div className="w-9 h-9 bg-indigo-100 text-indigo-700 flex items-center justify-center rounded border border-indigo-250 shrink-0">
                        <FileText className="w-4 h-4" />
                      </div>
                    )}
                    <div className="text-right">
                      <p className="font-bold text-slate-800 text-[11px] truncate max-w-[200px]">{attachedFile.name}</p>
                      <p className="text-[9px] text-slate-500 mt-0.1 font-mono">
                        {(attachedFile.size / 1024).toFixed(1)} KB | {attachedFile.type || "unknown/format"}
                      </p>
                    </div>
                  </div>
                  
                  <button 
                    type="button"
                    onClick={removeAttachedFile}
                    className="p-1.5 text-slate-400 hover:text-rose-500 rounded hover:bg-rose-50 transition cursor-pointer"
                    title="Remove attached file"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}

              {/* Single Input Bar */}
              <form onSubmit={handleSendMessage} className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-xl p-1.5 shadow-sm" dir="ltr">
                <input
                  ref={fileInputRef}
                  id="professor-doc-upload"
                  type="file"
                  accept="image/*,application/pdf,text/plain"
                  className="hidden"
                  onChange={handleFileChange}
                />
                
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="p-2 text-indigo-500 hover:bg-indigo-100 rounded-lg transition-colors cursor-pointer"
                  title="Attach Documents or Images"
                >
                  <Plus className="w-5 h-5 shrink-0" />
                </button>

                <input
                  id="professor-message-input"
                  type="text"
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  placeholder="Ask a clinical question or request drug interactions..."
                  className="flex-1 bg-transparent p-2 text-xs font-medium text-left outline-none placeholder-slate-400"
                  dir="ltr"
                />

                <button
                  type="submit"
                  disabled={isLoading || (!inputText.trim() && !attachedFile)}
                  className={`bg-indigo-600 hover:bg-indigo-500 text-white p-2 rounded-lg transition-colors flex items-center justify-center shrink-0 cursor-pointer ${
                    isLoading || (!inputText.trim() && !attachedFile) ? "opacity-50 cursor-not-allowed bg-slate-400" : ""
                  }`}
                  title="Send Inquiry"
                >
                  <Send className="w-4 h-4 ml-0.5" />
                </button>
              </form>

              {/* Bottom Safety Disclaimer Block */}
              <div className="bg-amber-50/60 border border-amber-100 rounded-lg p-2 flex items-start gap-1.5 text-left" dir="ltr">
                <ShieldAlert className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                <div>
                  <h5 className="text-[9.5px] font-black text-amber-900 uppercase">
                    CDSS Safety Alert & Professional Awareness
                  </h5>
                  <p className="text-[9px] text-amber-800 leading-relaxed mt-0.5 font-medium">
                    All clinical assistant analyses and developmental judgments are designed solely to support clinical decision-making. The diagnosis does not replace direct examination; full responsibility for choosing the path and dispensing medication lies with the attending, licensed physician.
                  </p>
                </div>
              </div>

            </div>

          </div>
        </div>
      )}

    </div>
  );
}
